<template>
  <div class="kb" :class="{ 'kb--mobile': isMobile }">
    <section
      v-for="col in columns"
      :key="col.key"
      class="kb__col"
      :class="{ 'kb__col--closed': col.kind && col.kind !== 'open' }"
      :style="{ '--cc': col.color || '#9aa4b2' }"
    >
      <header class="kb__head">
        <div class="kb__head-row">
          <span class="kb__dot" />
          <span class="kb__name" :title="col.title">{{ col.title }}</span>
          <span class="kb__count">{{ num(col.count) }}</span>
          <v-spacer />
          <button v-if="canAdd" type="button" class="kb__add" :title="$t('crmAdd')" @click="$emit('add', col)"><v-icon small>mdi-plus</v-icon></button>
        </div>
        <div v-if="showAmount" class="kb__sum">
          <template v-if="col.amount === null || col.amount === undefined"><v-icon x-small>mdi-lock-outline</v-icon></template>
          <template v-else>
            <b>{{ money(col.amount, true) }}</b>
            <span v-if="col.weighted" class="kb__weighted" :title="$t('crmWeighted')">≈ {{ money(col.weighted, true) }}</span>
          </template>
        </div>
        <div class="kb__bar"><i :style="{ width: share(col) + '%' }" /></div>
      </header>

      <div class="kb__scroll" @scroll="onScroll($event, col)">
        <draggable
          :list="col.items"
          class="kb__list"
          :group="group"
          :animation="180"
          ghost-class="kb__ghost"
          chosen-class="kb__chosen"
          :delay="isMobile ? 220 : 0"
          :delay-on-touch-only="true"
          :data-col="col.key"
          @end="onEnd"
        >
          <div v-for="item in col.items" :key="item.id" class="kb__item" :data-id="item.id">
            <record-card
              :item="item"
              :type="type"
              :color="col.color"
              :move-targets="targetsFor(col)"
              :show-status="type === 'deal' && item.status === 'paused'"
              @open="(it, tab) => $emit('open', it, tab)"
              @move="(it, t) => $emit('move', it, t, col)"
              @log="(it, kind, mode) => $emit('log', it, kind, mode)"
              @convert="$emit('convert', $event)"
            />
          </div>
        </draggable>
        <div v-if="!col.items.length && !col.loading" class="kb__empty">
          <v-icon small>mdi-tray-arrow-down</v-icon>{{ $t('crmDropHere') }}
        </div>
        <div v-if="col.loading" class="kb__loading"><v-progress-circular :size="18" :width="2" indeterminate color="primary" /></div>
        <button v-else-if="col.hasMore" type="button" class="kb__more" @click="$emit('load-more', col)">{{ $t('crmLoadMore') }}</button>
      </div>
    </section>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import RecordCard from './RecordCard'
import { money } from '../crmStore'

export default {
  name: 'CrmBoard',
  components: { draggable, RecordCard },
  props: {
    columns: { type: Array, required: true },
    type: { type: String, default: 'deal' },
    group: { type: String, default: 'crm' },
    showAmount: Boolean,
    canAdd: { type: Boolean, default: true }
  },
  computed: {
    maxCount () { return Math.max(1, ...this.columns.map(c => Number(c.count) || 0)) }
  },
  methods: {
    money,
    num (v) { return Number(v || 0).toLocaleString(this.$i18n.locale === 'en' ? 'en-US' : 'fa-IR') },
    share (col) { return Math.round(((Number(col.count) || 0) / this.maxCount) * 100) },
    targetsFor (col) {
      return this.columns.filter(c => c.key !== col.key).map(c => ({ key: c.key, label: c.title, color: c.color, target: c }))
    },
    onScroll (e, col) {
      const el = e.target
      if (col.hasMore && !col.loading && el.scrollTop + el.clientHeight > el.scrollHeight - 120) this.$emit('load-more', col)
    },
    onEnd (evt) {
      const fromKey = evt.from.dataset.col
      const toKey = evt.to.dataset.col
      if (fromKey === toKey) return
      const id = Number(evt.item.dataset.id)
      const from = this.columns.find(c => String(c.key) === fromKey)
      const to = this.columns.find(c => String(c.key) === toKey)
      const item = to.items.find(i => i.id === id)
      this.$emit('drop', { item, from, to, newIndex: evt.newIndex, oldIndex: evt.oldIndex })
    }
  }
}
</script>

<style scoped>
.kb { display: flex; gap: 12px; height: 100%; min-height: 0; overflow-x: auto; overflow-y: hidden; padding-bottom: 6px; }
.kb__col { flex: 0 0 286px; display: flex; flex-direction: column; min-height: 0; background: rgba(15, 23, 42, .035); border-radius: 6px; }
.theme--dark .kb__col { background: rgba(255, 255, 255, .04); }
.kb__col--closed { flex-basis: 250px; }
.kb__head { padding: 10px 12px 8px; flex-shrink: 0; }
.kb__head-row { display: flex; align-items: center; gap: 8px; }
.kb__dot { width: 9px; height: 9px; border-radius: 50%; background: var(--cc); flex-shrink: 0; }
.kb__name { font-weight: 700; font-size: .85rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.kb__count { font-size: .7rem; font-weight: 700; padding: 1px 7px; border-radius: 6px; background: rgba(127, 127, 127, .16); }
.kb__add { width: 26px; height: 26px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; }
.kb__add:hover { background: rgba(127, 127, 127, .15); }
.kb__sum { display: flex; align-items: baseline; gap: 8px; margin-top: 4px; font-size: .8rem; }
.kb__weighted { font-size: .7rem; opacity: .6; }
.kb__bar { height: 3px; border-radius: 3px; background: rgba(127, 127, 127, .14); margin-top: 8px; overflow: hidden; }
.kb__bar i { display: block; height: 100%; background: var(--cc); border-radius: 3px; transition: width .3s; }
.kb__scroll { flex: 1; min-height: 0; overflow-y: auto; overflow-x: hidden; padding: 2px 8px 10px; }
.kb__list { display: flex; flex-direction: column; gap: 8px; min-height: 60px; }
.kb__item { flex-shrink: 0; }
.kb__ghost { opacity: .35; }
.kb__ghost ::v-deep .rc { border: 2px dashed var(--v-primary-base); box-shadow: none; }
.kb__chosen ::v-deep .rc { box-shadow: 0 10px 24px rgba(15, 23, 42, .18); transform: rotate(1.5deg); }
.kb__empty { display: flex; align-items: center; justify-content: center; gap: 6px; margin-top: -52px; height: 52px; font-size: .75rem; opacity: .5; border: 1px dashed rgba(127, 127, 127, .4); border-radius: 6px; pointer-events: none; }
.kb__loading { display: flex; justify-content: center; padding: 10px; }
.kb__more { width: 100%; margin-top: 8px; height: 30px; border-radius: 6px; font-size: .76rem; color: var(--v-primary-base); }
.kb__more:hover { background: rgba(25, 118, 210, .08); }
.kb--mobile { scroll-snap-type: x mandatory; gap: 10px; }
.kb--mobile .kb__col { flex-basis: 86vw; scroll-snap-align: center; }
</style>
